/* The team. VERBATIM from PRD §4 + Brandon's q.01/q.05 answers (31 Jul pack).
 *
 * Titles are descriptive and carry ZERO permission effect — the PRD is explicit
 * about this and the code must never branch on `title`.
 *
 * There are exactly two per-person permissions in the entire product:
 *   · Patrick Wood may rectify the priority order (Brandon's q.05).
 *   · Brandon Wilson has the admin flag — which is a flag on a normal account,
 *     not a separate access model. He captures and gets assigned like anyone.
 * Both are enforced SERVER-SIDE. The flags here only decide what gets drawn.
 *
 * Stuart Hutchin is the QP who signs off public disclosure. He appears in
 * compliance copy and is NOT a platform user. Do not create an account.
 */

import type { User } from './types';

export const ROSTER: User[] = [
  {
    id: 'u-patrick-wood',
    name: 'Patrick Wood',
    initials: 'PW',
    title: 'Lead',
    role: 'geology_team_member',
    email: 'patrick@kilroymetal.example', // SWAP — emails not supplied
    active: true,
    can_rectify: true,
  },
  {
    id: 'u-michaela-schnell',
    name: 'Michaela Schnell',
    initials: 'MS',
    title: 'PhD Research',
    role: 'geology_team_member',
    email: 'michaela@kilroymetal.example',
    active: true,
  },
  {
    id: 'u-gabe-sanchez',
    name: 'Gabe Sanchez',
    initials: 'GS',
    title: 'Masters Research',
    role: 'geology_team_member',
    email: 'gabe@kilroymetal.example',
    active: true,
  },
  {
    id: 'u-allan-miller',
    name: 'Allan Miller',
    initials: 'AM',
    title: 'Geology Consultant',
    role: 'geology_team_member',
    email: 'allan@kilroymetal.example',
    active: true,
  },
  {
    id: 'u-thomas-monecke',
    name: 'Thomas Monecke',
    initials: 'TM',
    title: 'Consulting Geologist',
    role: 'geology_team_member',
    email: 'thomas@kilroymetal.example',
    active: true,
  },
  {
    id: 'u-marta-codeco',
    name: 'Marta Codeco',
    initials: 'MC',
    title: 'Consulting Geologist',
    role: 'geology_team_member',
    email: 'marta@kilroymetal.example',
    active: true,
  },
  {
    id: 'u-brandon-wilson',
    name: 'Brandon Wilson',
    initials: 'BW',
    title: 'Admin / Product Owner',
    role: 'admin',
    email: 'brandon@kilroymetal.example',
    active: true,
  },
];

/**
 * Who mostly carries a phone (Addendum A: ~4 regular mobile users) versus who
 * lives on the desk. Used only to seed sensible demo data and to order the
 * assignee picker — never to restrict anything.
 */
export const MOBILE_REGULARS = [
  'u-patrick-wood',
  'u-gabe-sanchez',
  'u-michaela-schnell',
  'u-brandon-wilson',
];

/** Brandon's q.01, verbatim: "Patrick, Allan and Thomas in that order." */
export const JUDGMENT_ORDER = ['u-patrick-wood', 'u-allan-miller', 'u-thomas-monecke'];

export const userById = (id: string): User =>
  ROSTER.find((u) => u.id === id) ?? {
    id,
    name: id,
    initials: id.slice(0, 2).toUpperCase(),
    title: '',
    role: 'geology_team_member',
    email: '',
    active: false,
  };

export const canRectify = (id: string): boolean => !!userById(id).can_rectify;
export const isAdmin = (id: string): boolean => userById(id).role === 'admin';

/** Signed-in user for the demo build. Replaced by the magic-link session. */
export const DEMO_USER_ID = 'u-gabe-sanchez';
